import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { DeliveryWorkStatus } from '../../models/delivery-work';
import work from '../../services/orders/delivery-work.service';
import { captureDeliveryLocation, CapturedDeliveryLocation, DeliveryLocationError, DeliveryLocationIssue } from '../../services/orders/delivery-location.service';
import DeliveryLocationPanel from './DeliveryLocationPanel';

export interface DeliveryWorkControlsHandle { ensureStarted: (orderId: number) => Promise<boolean>; }
interface Props { onError: (message: string) => void; onStatusChange?: (status?: DeliveryWorkStatus) => void; }

const DeliveryWorkControls = forwardRef<DeliveryWorkControlsHandle, Props>(({ onError, onStatusChange }, ref) => {
  const [status, setStatus] = useState<DeliveryWorkStatus>();
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locationIssue, setLocationIssue] = useState<DeliveryLocationIssue>();
  const [locationReading, setLocationReading] = useState<CapturedDeliveryLocation>();
  const activeCapture = useRef<AbortController>();
  const working = useRef(false);
  const checking = useRef(false);
  const refreshVersion = useRef(0);
  const [prompt, setPrompt] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const pending = useRef<((started: boolean) => void) | null>(null);
  const mounted = useRef(true);
  const finish = useCallback((started: boolean) => {
    pending.current?.(started); pending.current = null;
    if (mounted.current) setPrompt(false);
  }, []);
  const refresh = useCallback(async () => {
    const version = ++refreshVersion.current;
    try { const value = await work.today(); if (mounted.current && version === refreshVersion.current) { setStatus(value); onStatusChange?.(value); } }
    catch { if (mounted.current && version === refreshVersion.current) {
      setStatus(undefined); onStatusChange?.(undefined);
      onError('Could not load delivery Start/End status. Refresh before generating an invoice.');
    } }
  }, [onError, onStatusChange]);
  useEffect(() => {
    mounted.current = true;
    void refresh();
    const onFocus = () => { void refresh(); };
    window.addEventListener('focus', onFocus);
    // Refresh at day boundaries and reflect another tab's Start/End.
    const timer = window.setInterval(onFocus, 60000);
    return () => { mounted.current = false; activeCapture.current?.abort(); finish(false); window.clearInterval(timer); window.removeEventListener('focus', onFocus); };
  }, [refresh, finish]);
  useImperativeHandle(ref, () => ({ ensureStarted: async (orderId: number) => {
    if (working.current || checking.current || pending.current) return false;
    checking.current = true;
    try {
      const check = await work.invoiceCheck(orderId);
      if (!mounted.current) return false;
      if (!check.startRequired) return true;
      setPrompt(true);
      return await new Promise<boolean>(resolve => { pending.current = resolve; });
    } catch { onError('Could not check delivery status. Please retry before generating this invoice.'); return false; }
    finally { checking.current = false; }
  } }), [onError]);

  const change = async (action: 'start' | 'end') => {
    if (working.current) return;
    working.current = true; setBusy(true); setLocating(true); setLocationIssue(undefined); setLocationReading(undefined);
    const controller = new AbortController(); activeCapture.current = controller;
    try {
      const value = await work.change(action, { signal: controller.signal, onCaptured: () => { if (mounted.current) setLocating(false); } });
      if (!mounted.current) return;
      refreshVersion.current++;
      setStatus(value); setConfirmEnd(false);
      onStatusChange?.(value);
      if (action === 'start') finish(!!value.session?.startedAtUtc);
    } catch (failure) {
      if (mounted.current && !controller.signal.aborted) {
        if (failure instanceof DeliveryLocationError) {
          setLocationIssue(failure.issue);
          if (action === 'start') setPrompt(true);
        } else onError('Could not confirm delivery activity. Refresh the status before trying again.');
      }
    } finally { working.current = false; if (mounted.current) { setBusy(false); setLocating(false); } }
  };
  const checkLocation = async () => {
    if (working.current) return;
    working.current = true; setBusy(true); setLocating(true); setLocationIssue(undefined); setLocationReading(undefined);
    const controller = new AbortController(); activeCapture.current = controller;
    try {
      const reading = await captureDeliveryLocation(controller.signal);
      if (mounted.current) setLocationReading(reading);
    } catch (failure) {
      if (mounted.current && failure instanceof DeliveryLocationError) setLocationIssue(failure.issue);
    } finally { working.current = false; if (mounted.current) { setBusy(false); setLocating(false); } }
  };
  const locationPanel = <DeliveryLocationPanel issue={locationIssue} reading={locationReading} locating={locating}
    disabled={busy} onCheck={() => void checkLocation()} />;

  return <>
    {status?.isExecutive && <div className="flex flex-wrap align-items-center gap-2 my-3" aria-label="Delivery working day">
      <span>Delivery {status.deliveryDate}: <strong>{status.session?.status ?? 'Not started'}</strong></span>
      <Button label="Start Delivery" icon="pi pi-play" disabled={busy || !!status.session} loading={busy && !status.session}
        onClick={() => void change('start')} />
      <Button label="End Delivery" icon="pi pi-stop" severity="warning" disabled={busy || !status.session || !!status.session.endedAtUtc}
        onClick={() => setConfirmEnd(true)} />
      {status.session?.startLocationReason && <span className="text-sm">Start location: {status.session.startLocationReason}</span>}
      {status.session?.endLocationReason && <span className="text-sm">End location: {status.session.endLocationReason}</span>}
      {!prompt && !confirmEnd && !status.session?.endedAtUtc && locationPanel}
      {busy && <span role="status">{locating ? 'Getting your location…' : 'Saving delivery activity…'}</span>}
    </div>}
    <Dialog header="Start delivery" visible={prompt} onHide={() => { if (!busy) finish(false); }} closable={!busy}
      style={{ width: '32rem', maxWidth: '95vw' }} modal
      footer={<><Button label="Cancel" outlined disabled={busy} onClick={() => finish(false)} />
        <Button label="Start Delivery" icon="pi pi-play" loading={busy} onClick={() => void change('start')} /></>}>
      <p>You have not started delivery today. Start delivery before generating your first assigned-order invoice.</p>
      <p>A fresh location is required. Start is recorded only after your location and start time are saved successfully.</p>
      {locationPanel}
    </Dialog>
    <Dialog header="End delivery" visible={confirmEnd} onHide={() => { if (!busy) setConfirmEnd(false); }} closable={!busy}
      style={{ width: '30rem', maxWidth: '95vw' }} modal
      footer={<><Button label="Cancel" outlined disabled={busy} onClick={() => setConfirmEnd(false)} />
        <Button label="End Delivery" severity="warning" loading={busy} onClick={() => void change('end')} /></>}>
      <p>Record the end of today's delivery? A fresh location is required. Your end time and location must be saved successfully. This day cannot be restarted.</p>
      {locationPanel}
    </Dialog>
  </>;
});
export default DeliveryWorkControls;
